import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar, FileText } from "lucide-react";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Container = ({ children, className = "" }) => <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;

export default function Services() {
  const [services, setServices] = useState([]);
  const [sel, setSel] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/services").then(({ data }) => setServices(data || [])); }, []);

  return (
    <div>
      <div className="bg-white border-b border-border py-16 md:py-20">
        <Container>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Nos services</span>
          <h1 className="mt-3 font-heading font-bold tracking-tight text-slate-900 text-4xl sm:text-5xl">Des services pensés pour votre cabinet</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">Sélectionnez un ou plusieurs services. Certains peuvent être réservés au calendrier, d'autres font l'objet d'une demande personnalisée.</p>
        </Container>
      </div>
      <Container className="py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.service_id} data-testid={`service-card-${s.service_id}`}
              className="group bg-white rounded-xl border border-slate-200/90 shadow-sm hover:shadow-md hover:border-primary/30 transition-all p-6 flex flex-col">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center"><Icon name={s.icon} className="h-6 w-6 text-primary" /></div>
              <h3 className="mt-5 font-heading font-semibold text-lg text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{s.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.bookable && <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50"><Calendar className="h-3 w-3 mr-1" />Réservable</Badge>}
                {s.custom_request && <Badge variant="outline" className="text-slate-600"><FileText className="h-3 w-3 mr-1" />Sur demande</Badge>}
              </div>
              <div className="mt-5 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSel(s)} data-testid={`service-detail-${s.service_id}`}>Détails</Button>
                <Button size="sm" className="bg-primary" onClick={() => navigate(`/demande?service=${encodeURIComponent(s.title)}`)}>{s.cta || "Faire une demande"}</Button>
              </div>
            </div>
          ))}
        </div>
      </Container>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          {sel && (<>
            <DialogHeader>
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-2"><Icon name={sel.icon} className="h-6 w-6 text-primary" /></div>
              <DialogTitle className="font-heading text-xl">{sel.title}</DialogTitle>
              <DialogDescription className="text-slate-600 leading-relaxed pt-2">{sel.description}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap gap-2">
              {sel.bookable && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Réservable au calendrier</Badge>}
              {sel.custom_request && <Badge variant="outline">Demande personnalisée</Badge>}
            </div>
            <Button className="w-full bg-primary mt-2" onClick={() => navigate(`/demande?service=${encodeURIComponent(sel.title)}`)}>Faire une demande <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
